import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { on, getFocusables } from '@src/utils';
import { KEY_CODES } from '@src/utils/constants';

class Republisher {
  constructor() {
    this.body = document.querySelector('body');
    this.sidebar = document.querySelector('.js-sidebar');

    this.republisher = document.querySelector('.js-republisher');
    this.openButtonClass = '.js-open-republisher';
    this.openButton = document.querySelector(this.openButtonClass);
    this.openButtons = document.querySelectorAll(this.openButtonClass);

    this.republisherInner = this.getRepublishElem('.js-republisher-inner');
    this.closeButton = this.getRepublishElem('.js-close-republisher');
    this.markupContainer = this.getRepublishElem('.js-republish-markup');
    this.copyButton = this.getRepublishElem('.js-copy-button');
    this.contentContainer = this.getRepublishElem('.js-republish-content');
    this.articleHeading = this.getRepublishElem('.js-article-heading');
    this.articleDate = this.getRepublishElem('.js-article-date');
    this.articleByline = this.getRepublishElem('.js-article-byline');

    this.firstModalFocusable = this.getFirstEl(this.modalFocusables);
    this.lastModalFocusable = this.getLastEl(this.modalFocusables);

    this.firstSidebarFocusable = this.getFirstEl(this.sidebarFocusables);
    this.lastSidebarFocusable = this.getLastEl(this.sidebarFocusables);

    this.postId = this.republisher ? this.republisher.dataset.postId : null;
    this.publishYear = this.republisher ? this.republisher.dataset.publishYear : null;
    this.publishMonth = this.republisher ? this.republisher.dataset.publishMonth : null;
    this.publishMonth = this.publishMonth.padStart(2, '0');
    this.slug = this.republisher ? this.republisher.dataset.slug : null;

    this.bodyActiveClass = 'republish-modal-open';
    this.republisherActiveClass = 'republish--shown';
    this.isOpen = false;
    this.copiedClass = 'republish__copy-button--show-notifier';
    this.copyText = 'Copy HTML';
    this.copiedText = 'Copied!';
    this.notifierTimeout = null;
    this.notifierTiming = 1500;

    this.embeddedPostDataId = '#article-data';

    if (document.querySelector(this.embeddedPostDataId)) {
      this.embeddedPostData = JSON.parse(
        document.querySelector(this.embeddedPostDataId).textContent,
      );
    }

    this.postData = null;
    this.articleBylineMarkup = null;

    this.queryParam = '?republish';

    this.init();
    this.addEventListeners();
  }

  /**
   * Gets the blocks that are restricted from appearing within the republisher content.
   */
  get blockRestrictList() {
    return [
      '.wp-block-about-us-cta',
      '.wp-block-newsletter-cta',
      '.wp-block-join-cta',
      '.wp-block-mini-banner',
      '.wp-block-decision-desk-hq',
      // The datawrapper block is now allowed: 
      // '.wp-block-embed-datawrapper',
    ];
  }

  get modalFocusables() {
    return getFocusables(this.republisher);
  }

  get sidebarFocusables() {
    return getFocusables(this.sidebar);
  }

  getFirstEl(arr) {
    return arr[0];
  }

  getLastEl(arr) {
    return arr[arr.length - 1];
  }

  getRepublishElem(selector) {
    return this.republisher ? this.republisher.querySelector(selector) : null;
  }

  init() {
    if (document.location.search === this.queryParam) {
      this.openModal();
    }
  }

  addEventListeners() {
    on(this.openButtons, 'click', e => this.openModal(e));
    on(this.closeButton, 'click', () => this.closeModal());
    on(this.copyButton, 'click', () => this.copy());
    on(this.republisher, 'keydown', e => this.handleModalKeydown(e));
    on(this.sidebar, 'keydown', e => this.handleSidebarKeydown(e));

    // Close the modal when window history changes
    window.onpopstate = () => {
      if (!document.location.search && this.isOpen) {
        this.closeModal();
      }
    };
  }

  /**
   * Fetch republish content via WP REST API
   *
   * @see https://developer.wordpress.org/rest-api/reference/posts
   *
   * @param {String} postId the ID of the post to fetch
   *
   * @return void
   */
  getContent(postId) {
    const apiUrl = `/wp-json/wp/v2/posts/${postId}`;

    fetch(apiUrl)
      .then(response => response.json())
      .then(post => {
        this.postData = post;
        return this.getBylines(post.meta.bylines);
      })
      .then(bylines => {
        // Save markup to be used in other places
        this.articleBylineMarkup = bylines
          .map(author => `<a href="${author.link}">${author.name}</a>`)
          .join(', ');

        // Use non-link version to prevent screen reader confusions
        this.articleByline.textContent = bylines.map(author => author.name).join(', ');

        this.articleHeading.textContent = this.postData.title.rendered;
        this.articleDate.textContent = this.formatDate(this.postData.date);
        this.markupContainer.value = this.buildRepublishContent(this.postData);
        this.contentContainer.dataset.filled = true;
      })
      .catch(e => console.error(e));
  }

  /**
   * Fetch republish content from data embedded as JSON in the page
   *
   * @return true|false
   */
  async getContentFromEmbedded() {
    if (!this.embeddedPostData) {
      return false;
    }

    this.postData = {
      title: {
        rendered: this.embeddedPostData.post.post_title,
      },
      content: {
        rendered: this.embeddedPostData.post.post_content.replace(/<!--.*?-->/g, ''),
      },
      date: this.embeddedPostData.post.post_date,
    };

    this.articleBylineMarkup = this.embeddedPostData.authors
      .map(author => `<a href="${author.link}">${author.name}</a>`)
      .join(', ');

    // Call a function that takes an argument of author slugs and uses them to fetch pronouns from the REST API
    const bylineMarkup = await this.buildRepublishBylineMarkup(this.embeddedPostData.authors);
    


    // Use non-link version to prevent screen reader confusions
    this.articleByline.textContent = this.embeddedPostData.authors
      .map(author => author.name)
      .join(', ');
    
    

    this.articleHeading.textContent = this.postData.title.rendered;
    this.articleDate.textContent = this.formatDate(this.postData.date);
    this.markupContainer.value = await this.buildRepublishContent(this.postData, bylineMarkup);

    this.contentContainer.dataset.filled = true;

    return true;
  }

  
  
  /**
   * Use author slugs to get author pronouns and personal form of address
   *
   * @see 
   *
   * @param {String} slug of each author
   *
   * @return {Array} author pronoun and personal form of address, if there is one 
   */
  async getAuthorPronounAndPFA(slug) {
    const apiUrl = `/wp-json/wp/v2/authors?slug=${slug}`;
  
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
  
      const authors = await response.json();
      if (!authors || authors.length === 0) {
        throw new Error("Author not found");
      }
  
      const author = authors[0];
      const { acf, name } = author;

      let pronoun;
      let pfa;

      if (!acf?.staff_pronoun_sentence) {
        pronoun = "their";
      } else {
        pronoun = acf.staff_pronoun_sentence;
      }

      if (acf.personal_name) {
        pfa = acf.personal_name;
      } else {
        pfa = name.split(" ")[0];
      }
  
      return {
        pronoun,
        pfa,
      };
    } catch (error) {
      console.error("Error fetching author data:", error);
      throw error; 
    }
  }

  /**
   * Use post data to check whether there should be republishing byline markup intro copy. 
   * 
   * @param {Array} post the post data
   * 
   * @return {Boolean} should we include republishing byline markup intro copy?
   */
  async excludeRepublishBylineMarkup(postId) {
      const apiUrl = `/wp-json/wp/v2/posts/${postId}`;
    
      try {
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }
    
        const data = await response.json();
        if (!data || data.length === 0) {
          throw new Error("Post not found");
        }

        if (data.acf.exclude_republisher_intro_copy) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error("Error fetching author data:", error);
        throw error; 
      }

  }

  /**
  * Use post data to build byline markup for republisher
  *
  *
  * @param {Array} authors the author data 
  *
  * @return {String} the string for the republish byline markup 
  */
  async buildRepublishBylineMarkup(authors) {
    const url = new URL(`${document.location.origin}`);
    const utm = `?utm_source=partner&utm_medium=referral&utm_campaign=19th-republishing&utm_content=/${this.publishYear}/${this.publishMonth}/${this.slug}`;
    const thisStoryMarkup = `<p><em><a href="${url}${this.publishYear}/${this.publishMonth}/${this.slug}${utm}">This story</a> was originally reported by`;
    
    let markup;
  
    if (authors.length === 1) {
      const author = authors[0];
  
      if (author.name === "The 19th Staff") {
        const pfa = "the team";
        const pronoun = "The 19th's";
  
        markup =
          thisStoryMarkup +
          `<a href="${url}${utm}">The 19th</a>. <a href="${url}team${utm}"> Meet ${pfa} and read more of The 19th's reporting on gender, politics and policy</a>.</em></p>`;
      } else {
        try {
          const data = await this.getAuthorPronounAndPFA(author.slug);
          const pfa = data.pfa;
          const pronoun = data.pronoun;
  
          markup =
            thisStoryMarkup +
            ` ${author.name} of <a href="${url}${utm}">The 19th</a>. <a href="${url}author/${author.slug}${utm}"> Meet ${pfa} and read more of ${pronoun} reporting on gender, politics and policy</a>.</em></p>`;
        } catch (error) {
          console.error("Error fetching author data:", error);
        }
      }
    } else {

      const authorPromises = authors.map(async (author) => {
        try {
          const data = await this.getAuthorPronounAndPFA(author.slug);
          return {
            fullName: author.name,
            link: `<a href="${url}author/${author.slug}${utm}">${data.pfa}</a>`,
          };
        } catch (error) {
          console.error("Error fetching author data:", error);
          return null;
        }
      });
      
      const results = await Promise.all(authorPromises);
      
      const fullNamesArray = results.map((result) => result.fullName);
      const linksArray = results.map((result) => result.link);
      
      const fullNames = fullNamesArray.length > 1
        ? fullNamesArray.slice(0, -1).join(", ") + " and " + fullNamesArray.slice(-1)
        : fullNamesArray[0] || "";
      
      const links = linksArray.length > 1
        ? linksArray.slice(0, -1).join(", ") + " and " + linksArray.slice(-1)
        : linksArray[0] || "";
      
      markup = thisStoryMarkup + ` ` + fullNames + ` of <a href="${url}${utm}">The 19th</a>. Meet ` + links + ` and read more of their reporting on gender, politics and policy.</em></p>`;

    }
    return markup;
  }
  /**
   * Format timestamp to be human redable
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
   *
   * @param {String} date date field from WP post
   *
   * @return {String} date string
   */
  formatDate(date) {
    const d = new Date(date);
    const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(d);
    const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(d);
    const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d);
    return `${month} ${day}, ${year}`;
  }

  /**
   * Cleans up the article content fetched via the WordPress REST API. This removes
   * a few unwanted blocks, as well as attributes that won't be used by other news
   * sources on republish.
   *
   * @param {string} content the rendered article content from WordPress
   *
   * @returns {string} the cleaned content
   */
  cleanArticleContent(content) {
    const domParser = new DOMParser();

    const doc = domParser.parseFromString(content, 'text/html');
    doc.body.querySelectorAll(this.blockRestrictList.join(', ')).forEach(node => {
      node.parentNode.removeChild(node);
    });

    let articleContent = doc.body.innerHTML;

    // Remove classes
    articleContent = articleContent.replace(/\s+?(class="[^"]*")/g, '');
    // Remove extra line breaks
    articleContent = articleContent.replace(/\n+/g, '\n');
    // Remove data-* attributes (for images and tables)
    articleContent = articleContent.replace(/\s+?(data-([\w]+)="[^"]*")/g, '');
    // Remove sizes and srcset attributes for images
    articleContent = articleContent.replace(/\s+?(sizes="[^"]*")/g, '');
    articleContent = articleContent.replace(/\s+?(srcset="[^"]*")/g, '');

    return articleContent;
  }

  /**
   * Concatenate markup to be used as republish content
   *
   * @param {Object} WP Post object
   *
   * @return {String} String to be injected as textarea value
   */
  async buildRepublishContent(post, bylineMarkup) {
    const creativeCommons = [
      '<!--',
      'Creative Commons Attribution-NonCommercial-NoDerivatives',
      'https://creativecommons.org/licenses/by-nc-nd/4.0/',
      '-->',
    ].join('\n');


    const originalUrl = new URL(`${document.location.origin}${document.location.pathname}`);
    const excludeRepublishIntro = await this.excludeRepublishBylineMarkup(this.postId);
    const articleOrigin = excludeRepublishIntro ? '' : bylineMarkup;
    const articleContent = this.cleanArticleContent(post.content.rendered);
    const pixelTracker = `<img id="republication-tracker-tool-source" src="https://pixel.19thnews.org/${this.publishYear}/${this.publishMonth}/${this.slug}" alt="" />`;

    const republishContent = [creativeCommons, articleOrigin, articleContent, pixelTracker]
      .filter(Boolean)
      .map(markup => markup.trim())
      .join('\n');

    return republishContent;
  }

  /**
   * Fetch article bylines via WP REST API
   *
   * @param {number[]} authorIds Author IDs to query with
   *
   * @return {Promise<object[]>} promise which resolves to the authors in the byline
   */
  getBylines(authorIds) {
    const apiUrl = `/wp-json/wp/v2/authors?include=${encodeURIComponent(
      authorIds.join(','),
    )}&orderby=include`;

    return fetch(apiUrl)
      .then(response => response.json())
      .catch(e => console.error(e));
  }

  /**
   * Open the republishing modal.
   *
   * @return void
   */
  openModal(e = null) {
    this.getContentFromEmbedded();
    this.isOpen = true;
    this.body.classList.add(this.bodyActiveClass);
    disableBodyScroll(this.republisherInner);
    this.republisher.classList.add(this.republisherActiveClass);
    this.republisher.setAttribute('aria-hidden', 'false');
    this.openButton = e ? e.target.closest(this.openButtonClass) : this.openButton;
    this.closeButton.focus();

    // Add query param
    if (window.history && history.pushState) {
      history.pushState({}, '', this.queryParam);
    }

    // Send event to Google Tag Manager
    // dataLayer is defined in head.twig, on production
    window.dataLayer.push({
      event: 'republishOpen',
    });
  }

  /**
   * Close the republishing modal.
   *
   * @return void
   */
  closeModal(preventButtonFocus = false) {
    this.isOpen = false;
    this.body.classList.remove(this.bodyActiveClass);
    enableBodyScroll(this.republisherInner);
    this.republisher.classList.remove(this.republisherActiveClass);
    this.republisher.setAttribute('aria-hidden', 'true');

    // Do not focus open button if this function was called via opening the menu
    if (!preventButtonFocus) {
      this.openButton.focus();
    }

    // Remove query param
    if (window.history && history.pushState) {
      const originalUrl = new URL(`${document.location.origin}${document.location.pathname}`);
      history.replaceState({}, '', originalUrl);
    }
  }

  handleBackwardTab(evt) {
    if (document.activeElement === this.firstModalFocusable) {
      evt.preventDefault();
      this.lastModalFocusable.focus();
    }
  }

  handleForwardTab(evt) {
    if (document.activeElement === this.lastModalFocusable) {
      evt.preventDefault();
      this.firstModalFocusable.focus();
    }
  }

  /**
   * Handle keydown events within the republisher
   *
   * @param {Object} evt The event object from a keydown event
   *
   * @return void
   */
  handleModalKeydown(evt) {
    if (this.isOpen) {
      switch (evt.key) {
        case KEY_CODES.ESCAPE: {
          this.closeModal();
          break;
        }

        case KEY_CODES.TAB: {
          if (evt.shiftKey) {
            this.handleBackwardTab(evt);
          } else {
            this.handleForwardTab(evt);
          }
          break;
        }

        default: {
          break;
        }
      }
    }
  }

  /**
   * Handle keydown events for the sidebar
   * If the user manages to focus in the sidebar, force focus back into republish modal
   *
   * @param {Object} evt The event object from a keydown event
   *
   * @return void
   */
  handleSidebarKeydown(evt) {
    if (this.isOpen) {
      switch (evt.key) {
        case KEY_CODES.TAB: {
          if (evt.shiftKey) {
            if (document.activeElement === this.firstSidebarFocusable) {
              evt.preventDefault();
              this.firstModalFocusable.focus();
            }
          } else {
            if (document.activeElement === this.lastSidebarFocusable) {
              evt.preventDefault();
              this.firstModalFocusable.focus();
            }
          }
          break;
        }

        default: {
          break;
        }
      }
    }
  }

  /**
   * Shows the copied notifier
   *
   * @return void
   */
  showNotifier() {
    this.copyButton.classList.add(this.copiedClass);
    this.copyButton.textContent = this.copiedText;

    // Display alert for screen readers that HTML has been copied
    const alertNode = document.createElement('p');
    alertNode.className = 'visually-hidden';
    alertNode.setAttribute('role', 'alert');
    alertNode.textContent = 'HTML copied to clipboard';
    this.contentContainer.appendChild(alertNode);

    // Hide the notifier after a set amount of time.
    this.notifierTimeout = setTimeout(() => {
      // Revert copy button text
      this.copyButton.classList.remove(this.copiedClass);
      this.copyButton.textContent = this.copyText;
      // Remove a11y alert
      this.contentContainer.removeChild(alertNode);
      // Refocus copy button
      this.copyButton.focus();
    }, this.notifierTiming);
  }

  /**
   * Copy the article text
   *
   * @return void
   */
  copy() {
    let copySuccess = false;

    // Clear the timeout that hides the notifier
    clearTimeout(this.notifierTimeout);

    try {
      this.markupContainer.select();
      document.execCommand('copy');
      copySuccess = true;
    } catch (e) {
      console.error(e);
    }

    if (copySuccess) {
      this.showNotifier();
    }
  }
}

export default Republisher;
